import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MenuItem } from './menu.model';
import { LOCAL_MENU } from './menu.local';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventType, AuthenticationResult } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);

  private menuSubject = new BehaviorSubject<MenuItem[]>([]);
  menu$ = this.menuSubject.asObservable();

  constructor() {
    // Initial build (page refresh / hard reload)
    this.buildMenu();

    // Rebuild menu after login / logout
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          msg =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS
        )
      )
      .subscribe(msg => {
        // ✅ CRITICAL FIX: set active account on login
        if (msg.eventType === EventType.LOGIN_SUCCESS) {
          const result = msg.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(result.account);
        }

        this.buildMenu();
      });
  }

  private buildMenu() {
    // ✅ Ensure active account exists (important for refresh)
    this.ensureActiveAccount();

    const menu = this.filterMenu(LOCAL_MENU).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );

    this.menuSubject.next(menu);
  }

  private ensureActiveAccount() {
    const instance = this.authService.instance;

    if (!instance.getActiveAccount()) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    }
  }

  private filterMenu(menu: MenuItem[]): MenuItem[] {
    return menu
      .filter(item => this.userHasAnyRole(item.roles))
      .map(item => ({
        ...item,
        children: item.children
          ? this.filterMenu(item.children)
          : undefined
      }))
      .filter(item => !item.children || item.children.length > 0);
  }

  /** ✅ Azure AD App Roles check */
private userHasAnyRole(required?: ReadonlyArray<string>): boolean {
  if (!required || required.length === 0) {
    return true;
  }

  const account = this.authService.instance.getActiveAccount();
  const claims = account?.idTokenClaims as any;
  const userRoles: ReadonlyArray<string> = claims?.roles ?? [];

  return required.some(role => userRoles.includes(role));
}
}